<template>
    <div class="flex h-auto bg-gray-900 text-white">

        <!-- Sidebar -->
        <aside class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg">
            <div class="flex items-center justify-center space-x-3">
                <img src="@/assets/logo.svg" alt="Syniac Logo" class="h-20">
            </div>
            <Navbar />
        </aside>

        <!-- Main Content -->
        <div class="flex-1 flex flex-col">
            <header class="h-16 bg-gray-800 flex items-center px-6 shadow-md">
                <h2 class="text-xl font-semibold">Manage User</h2>
            </header>

            <main class="p-6 flex-1">

                <div class="bg-gray-800 p-6 rounded-xl shadow-md flex flex-col h-full">

                    <h3 class="text-lg font-semibold mb-4">
                        User Information
                    </h3>

                    <!-- USER CARD -->
                    <div class="bg-gray-700 p-4 rounded-lg shadow-inner flex flex-col space-y-5">

                        <!-- Header -->
                        <div class="flex items-center space-x-4">
                            <div class="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-2xl font-bold">
                                {{ user.initials }}
                            </div>
                            <div>
                                <h4 class="text-xl font-semibold">{{ user.name }}</h4>
                                <p class="text-gray-300">{{ user.email }}</p>
                            </div>
                        </div>

                        <!-- STATS GRID -->
                        <div class="grid grid-cols-3 gap-4">
                            <div class="p-4 bg-gray-800 rounded-lg">
                              <p class="text-gray-400">Role</p>
                              <!-- Editable role select -->
                              <select
                                v-model="user.role"
                                class="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="support">Support</option>
                                <option value="noc">NOC</option>
                                <option value="billing">Billing</option>
                              </select>
                            </div>

                            <div class="p-4 bg-gray-800 rounded-lg">
                                <p class="text-gray-400">Status</p>
                                <p class="text-white font-semibold">
                                    {{ user.status }}
                                </p>
                            </div>

                            <div class="p-4 bg-gray-800 rounded-lg">
                                <p class="text-gray-400">Credit Balance</p>
                                <p class="text-green-400 font-semibold">
                                  ${{ Number(user.credit || 0).toFixed(2) }}
                                </p>
                            </div>
                        </div>

                        <!-- ADDRESS -->
                        <div class="bg-gray-800 p-4 rounded-lg flex flex-col space-y-2">
                          <p class="text-gray-400 mb-1">Address</p>
                        
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <input
                              v-model="user.address"
                              placeholder="Street Address"
                              class="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            />
                            <input
                              v-model="user.city"
                              placeholder="City"
                              class="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            />
                            <input
                              v-model="user.state"
                              placeholder="State"
                              class="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            />
                            <input
                              v-model="user.zip"
                              placeholder="ZIP"
                              class="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            />
                            <input
                              v-model="user.country"
                              placeholder="Country"
                              class="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            />
                          </div>
                        
                          <button
                            @click="saveAddress"
                            class="mt-2 px-4 py-2 bg-blue-500 rounded-lg hover:bg-blue-600 w-32"
                          >
                            Save Address
                          </button>
                        </div>

                        <!-- ACTION BUTTONS -->
                        <div class="flex flex-wrap gap-3 mt-2">
                            <button
                                @click="changeRole"
                                class="px-4 py-2 bg-blue-500 rounded-lg hover:bg-blue-600"
                            >
                                Change Role
                            </button>
                            
                            <button
                                @click="toggleBanUser"
                                class="px-4 py-2 bg-red-500 rounded-lg hover:bg-red-600"
                            >
                                {{ user.status === 'banned' ? 'Unban User' : 'Ban User' }}
                            </button>
                        </div>

                        <!-- CREDIT CONTROLS -->
                        <div class="flex gap-3 items-center mt-2">
                            <input
                                v-model.number="creditAmount"
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                class="w-32 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg
                                       focus:outline-none focus:border-blue-500"
                            />

                            <button
                                @click="addCredit"
                                class="px-4 py-2 bg-green-500 rounded-lg hover:bg-green-600"
                            >
                                Add Credit
                            </button>

                            <button
                                @click="removeCredit"
                                class="px-4 py-2 bg-yellow-500 rounded-lg hover:bg-yellow-600"
                            >
                                Remove Credit
                            </button>
                        </div>

                    </div>

                    <!-- RECENT ORDERS -->
                    <div class="mt-6">

                        <h4 class="text-lg font-semibold mb-2">
                            Recent Orders
                        </h4>

                        <div class="bg-gray-700 p-4 rounded-lg shadow-inner">

                            <p v-if="recentOrders.length === 0" class="text-gray-300">
                                No recent orders.
                            </p>

                            <ul v-else>
                                <li
                                    v-for="order in recentOrders"
                                    :key="order.id"
                                    class="mb-2"
                                >
                                    <span class="text-white font-semibold">
                                        #{{ order.id }}
                                    </span>
                                    –
                                    <span class="text-gray-300">
                                        ${{ order.amount }}
                                    </span>
                                    <span class="text-sm text-gray-400">
                                        ({{ formatDate(order.created_at) }})
                                    </span>
                                </li>
                            </ul>

                        </div>

                    </div>

                </div>

            </main>
        </div>

    </div>
</template>

<script>
import Navbar from "@/components/adminnav.vue";
import axios from "axios";

export default {
    name: "AdminManageUser",
    components: { Navbar },

    data() {
        return {
            user: {
                id: null,
                name: "",
                email: "",
                role: "",
                status: "",
                initials: "",
                credit: 0,
                address: "",
                city: "",
                state: "",
                zip: ""
            },
            orders: [],
            creditAmount: 0
        };
    },

    async created() {
        const userId = this.$route.params.id;

        await this.fetchUser(userId);
        await this.fetchOrders(userId);
    },

    computed: {
        recentOrders() {
        return [...this.orders] // clone first
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5);
        }
    },

    methods: {

        async fetchUser(userId) {
            try {
                const res = await axios.get(
                    `https://api.syniac.com:3000/api/admin/user/${userId}`,
                    { withCredentials: true }
                );

                this.user = res.data;

                this.user.initials = this.user.name
                   ?.split(" ")
                    ?.map(n => n[0])
                    ?.join("") || "U";

            } catch (err) {
                console.error("Error fetching user:", err);
            }
        },

        async fetchOrders(userId) {
            try {
                const res = await axios.get(
                    `https://api.syniac.com:3000/api/admin/user/${userId}/orders`,
                    { withCredentials: true }
                );

                this.orders = res.data;

            } catch (err) {
                console.error("Error fetching orders:", err);
            }
        },

        async changeRole() {
            try {
                if (!["user", "admin", "support", "noc", "billing"].includes(this.user.role)) {
                    alert("Invalid role selected.");
                    return;
                }
        
                await axios.put(
                    `https://api.syniac.com:3000/api/admin/user/${this.user.id}/role`,
                    { role: this.user.role },
                    { withCredentials: true }
                );
        
                alert("Role updated successfully!");
            } catch (err) {
                console.error("Error updating role:", err);
                alert("Failed to update role.");
            }
        },

        async toggleBanUser() {
            try {
                const newStatus = this.user.status === "banned" ? "active" : "banned";

                await axios.put(
                    `https://api.syniac.com:3000/api/admin/user/${this.user.id}/status`,
                    { status: newStatus },
                    { withCredentials: true }
                );

                this.user.status = newStatus;

            } catch (err) {
                console.error("Error toggling ban:", err);
            }
        },

        async addCredit() {
            if (!this.creditAmount || this.creditAmount <= 0) return;

            try {
                const res = await axios.put(
                    `https://api.syniac.com:3000/api/admin/user/${this.user.id}/credit/add`,
                    { amount: this.creditAmount },
                    { withCredentials: true }
                );

                this.user.credit = res.data.credit;
                this.creditAmount = 0;

            } catch (err) {
                console.error("Error adding credit:", err);
            }
        },

        async removeCredit() {
            if (!this.creditAmount || this.creditAmount <= 0) return;

            try {
                const res = await axios.put(
                    `https://api.syniac.com:3000/api/admin/user/${this.user.id}/credit/remove`,
                    { amount: this.creditAmount },
                    { withCredentials: true }
                );

                this.user.credit = res.data.credit;
                this.creditAmount = 0;

            } catch (err) {
                console.error("Error removing credit:", err);
            }
        },

        formatDate(date) {
            return new Date(date).toLocaleDateString("en-US");
        },

        async saveAddress() {
          try {
            const res = await axios.put(
              `https://api.syniac.com:3000/api/admin/user/${this.user.id}/address`,
              {
                address: this.user.address,
                line2: this.user.line2,
                city: this.user.city,
                state: this.user.state,
                zip: this.user.zip,
                country: this.user.country
              },
              { withCredentials: true }
            );
        
            if (res.data.success) {
              alert("Address saved successfully!");
            }
        
          } catch (err) {
            console.error("Error saving address:", err);
            alert("Failed to save address.");
          }
        }
        
    }
};
</script>
